/**
 * `RtcClient`: the Agora-shaped facade over a neutral media transport and the control-plane token
 * provider. See `docs/specs/rtc-sdk-contract.md`; every method there is implemented here.
 */
import { TOKEN_PRIVILEGE_WILL_EXPIRE_LEAD_SECONDS, } from '@nimeet/shared/browser';
import { Emitter } from './emitter.js';
import { normalizeError, rtcError } from './errors.js';
import { QualityReporter } from './quality.js';
import { classifyStats } from './stats.js';
import { RtcTokenProvider, parseTokenTiming, } from './token-provider.js';
import { FakeTransport, SfuTransport, } from './transport.js';
import { NetworkQuality, RTC_EVENT_NAMES, systemClock, } from './types.js';
const CHANNEL_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const LOG_LEVELS = { debug: 10, info: 20, warn: 30, error: 40 };
const DEFAULT_STATS_INTERVAL_MS = 2000;
function createLogger(level, custom) {
    if (custom)
        return custom;
    const threshold = LOG_LEVELS[level ?? 'warn'];
    const write = (target, args) => {
        if (LOG_LEVELS[target] < threshold)
            return;
        if (target === 'debug')
            console.debug(...args);
        else if (target === 'info')
            console.info(...args);
        else if (target === 'warn')
            console.warn(...args);
        else
            console.error(...args);
    };
    return {
        debug: (...args) => write('debug', args),
        info: (...args) => write('info', args),
        warn: (...args) => write('warn', args),
        error: (...args) => write('error', args),
    };
}
function publicStats(stats) {
    return {
        rtt: stats.rtt,
        packetLoss: stats.packetLoss,
        jitter: stats.jitter,
        bitrate: stats.bitrate,
        resolution: { width: stats.resolution.width, height: stats.resolution.height },
        framesPerSecond: stats.framesPerSecond,
        freezeCount: stats.freezeCount,
    };
}
function isoToEpochMs(value) {
    if (!value)
        return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
}
/**
 * RTC SDK facade. Construct with {@link createClient}.
 *
 * All rejections are {@link RtcError}; listener exceptions are isolated from the media loop.
 */
export class RtcClient {
    config;
    logger;
    clock;
    tokenProvider;
    emitter;
    reconnectBaseDelayMs;
    reconnectMaxDelayMs;
    reconnectMaxAttempts;
    statsIntervalMs;
    transport = null;
    transportMode;
    transportWired = false;
    state = 'disconnected';
    channelName = null;
    localUid = null;
    userId = null;
    role = 'host';
    token = null;
    joinRequest = null;
    tokenTimingHost = { privilegeExpireAt: null };
    participants = new Map();
    freezeCursor = new Map();
    localAudioPublished = false;
    localVideoPublished = false;
    localAudioMuted = false;
    localVideoMuted = false;
    localScreenShared = false;
    previewing = false;
    encoderConfig = null;
    dualStream = false;
    dynacast = false;
    visibleOnly = false;
    leaving = false;
    reconnectAttempt = 0;
    reconnectTimer = null;
    renewTimer = null;
    statsTimer = null;
    privilegeExpiredEmitted = false;
    privilegeWillExpireEmitted = false;
    lastStatsAt = null;
    lastError = null;
    dimensions;
    qualityReporter = null;
    pendingJoinObservation = null;
    constructor(config) {
        if (typeof config.appId !== 'string' || config.appId.length < 8 || config.appId.length > 64) {
            throw rtcError('invalid_appid', 'appId must be a string between 8 and 64 characters', {
                details: { appId: config.appId },
            });
        }
        if (!config.tokenProvider) {
            throw rtcError('invalid_parameter', 'tokenProvider is required');
        }
        this.config = config;
        this.logger = createLogger(config.logLevel, config.logger);
        this.clock = config.clock ?? systemClock;
        this.tokenProvider = new RtcTokenProvider(config.tokenProvider, {
            ...(config.uidMapping ? { uidMapping: config.uidMapping } : {}),
            clock: this.clock,
            ...(config.fetch ? { fetch: config.fetch } : {}),
        });
        this.emitter = new Emitter((error, event) => {
            this.logger.warn('rtc-web: event listener failed', String(event), error);
        });
        this.reconnectBaseDelayMs = Math.max(50, config.reconnect?.baseDelayMs ?? 500);
        this.reconnectMaxDelayMs = Math.max(this.reconnectBaseDelayMs, config.reconnect?.maxDelayMs ?? 8000);
        this.reconnectMaxAttempts = Math.max(1, Math.round(config.reconnect?.maxAttempts ?? 5));
        this.statsIntervalMs = Math.max(250, config.statsIntervalMs ?? DEFAULT_STATS_INTERVAL_MS);
        if (config.transport && typeof config.transport === 'object') {
            this.transport = config.transport;
            this.transportMode = null;
        }
        else {
            this.transportMode = config.transport ?? 'auto';
        }
        if (config.dimensions)
            this.dimensions = config.dimensions;
    }
    // ------------------------------------------------------------------ events
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    once(event, listener) {
        return this.emitter.once(event, listener);
    }
    off(event, listener) {
        this.emitter.off(event, listener);
    }
    getSupportedEvents() {
        return RTC_EVENT_NAMES;
    }
    // ------------------------------------------------------------------ lifecycle
    async joinChannel(channelName, options = {}) {
        if (this.state === 'connected' ||
            this.state === 'connecting' ||
            this.state === 'reconnecting') {
            throw rtcError('already_in_channel', 'already joined or joining a channel', { sdkCode: -19 });
        }
        if (typeof channelName !== 'string' || !CHANNEL_PATTERN.test(channelName)) {
            throw rtcError('invalid_channel_name', `invalid channel name: ${String(channelName)}`, {
                sdkCode: -102,
            });
        }
        const role = options.role ?? 'host';
        if (role === 'audience' && (options.publishAudio === true || options.publishVideo === true)) {
            throw rtcError('RTC_PUBLISH_DENIED', 'audience participants cannot publish media');
        }
        const publishAudio = options.publishAudio ?? role === 'host';
        const publishVideo = options.publishVideo ?? role === 'host';
        this.role = role;
        this.userId = options.userId ?? null;
        this.channelName = channelName;
        this.leaving = false;
        this.reconnectAttempt = 0;
        this.privilegeExpiredEmitted = false;
        this.pendingJoinObservation = null;
        this.clearReconnectTimer();
        this.stopTimers();
        const request = {
            channelName,
            ...(options.uid !== undefined ? { uid: options.uid } : {}),
            ...(options.userId !== undefined ? { userId: options.userId } : {}),
            role,
        };
        this.joinRequest = request;
        this.transition('connecting', 'join');
        const startedAt = this.clock.now();
        try {
            const token = await this.tokenProvider.getRTCTokenInfo(request);
            this.applyToken(token, channelName);
            const transport = this.obtainTransport();
            this.wireTransport(transport);
            const url = this.resolveServerUrl(token);
            if (!url && transport.kind !== 'fake') {
                throw rtcError('invalid_parameter', 'the media server URL is missing: return serverUrl from the token provider or set serverUrl in createClient()');
            }
            const result = await transport.connect(this.buildJoinOptions(token, channelName, url, publishAudio, publishVideo));
            this.localUid = result.uid;
            this.localAudioPublished = publishAudio;
            this.localVideoPublished = publishVideo;
            this.localAudioMuted = false;
            this.localVideoMuted = false;
            this.localScreenShared = false;
            this.participants.clear();
            this.freezeCursor.clear();
            this.lastStatsAt = this.clock.now();
            for (const participant of result.participants)
                await this.announceParticipant(participant);
            this.transition('connected', 'joined');
            this.startTimers();
            this.recordJoinObservation({
                delayMs: this.clock.now() - startedAt,
                success: true,
                within5s: this.clock.now() - startedAt <= 5000,
            });
        }
        catch (error) {
            this.stopTimers();
            this.transition('failed', error instanceof Error ? error.message : String(error));
            if (this.channelName !== null && this.localUid !== null) {
                this.recordJoinObservation({
                    delayMs: this.clock.now() - startedAt,
                    success: false,
                    within5s: false,
                });
            }
            this.lastError = normalizeError(error);
            this.channelName = null;
            this.localUid = null;
            this.token = null;
            this.joinRequest = null;
            throw this.lastError;
        }
    }
    async leaveChannel() {
        if (this.channelName === null && this.state === 'disconnected') {
            throw rtcError('channel_not_found', 'not in a channel', { sdkCode: -113 });
        }
        this.leaving = true;
        this.stopTimers();
        this.clearReconnectTimer();
        const transport = this.transport;
        const participants = [...this.participants.values()];
        try {
            if (transport) {
                if (this.localScreenShared) {
                    await transport.stopScreenShare().catch((error) => {
                        this.logger.debug('rtc-web: stopScreenShare during leave failed', error);
                    });
                }
                await transport.disconnect();
            }
        }
        catch (error) {
            this.logger.warn('rtc-web: transport disconnect failed during leave', error);
        }
        finally {
            for (const participant of participants) {
                this.emit('user-left', {
                    uid: participant.uid,
                    userId: participant.userId,
                    reason: 'leave',
                });
            }
            this.participants.clear();
            this.freezeCursor.clear();
            if (this.qualityReporter) {
                const reporter = this.qualityReporter;
                this.qualityReporter = null;
                await reporter.stop();
            }
            this.channelName = null;
            this.localUid = null;
            this.token = null;
            this.joinRequest = null;
            this.userId = null;
            this.localAudioPublished = false;
            this.localVideoPublished = false;
            this.localAudioMuted = false;
            this.localVideoMuted = false;
            this.localScreenShared = false;
            this.lastStatsAt = null;
            this.transition('disconnected', 'leave');
            this.leaving = false;
        }
    }
    /** Renew the current credential via `/v1/tokens/renew` (or re-resolution). */
    async renewToken() {
        if (!this.token || !this.joinRequest) {
            throw rtcError('channel_not_found', 'not in a channel', { sdkCode: -113 });
        }
        const renewed = await this.tokenProvider.renewToken(this.token, this.joinRequest);
        this.applyToken(renewed, this.channelName ?? this.joinRequest.channelName);
        if (this.transport)
            await this.transport.renewToken(renewed.rtcToken);
        this.scheduleRenewal();
        return renewed;
    }
    /** Apply a caller-supplied token (Agora's `renewToken(token)` semantics). */
    async renewTokenWith(token) {
        if (!this.token || !this.joinRequest) {
            throw rtcError('channel_not_found', 'not in a channel', { sdkCode: -113 });
        }
        if (typeof token !== 'string' || token.length === 0) {
            throw rtcError('invalid_token', 'token must be a non-empty string');
        }
        const renewed = { ...this.token, rtcToken: token };
        this.applyToken(renewed, this.channelName ?? this.joinRequest.channelName);
        if (this.transport)
            await this.transport.renewToken(token);
        this.scheduleRenewal();
    }
    /**
     * Local role switch. Audience members stop publishing immediately; the media edge applies the
     * new publish permission on the next credential refresh.
     */
    async setRole(role) {
        if (role !== 'host' && role !== 'audience') {
            throw rtcError('invalid_parameter', "role must be 'host' or 'audience'");
        }
        if (this.state !== 'connected' && this.state !== 'reconnecting') {
            throw rtcError('channel_not_found', 'join a channel before switching roles', {
                sdkCode: -113,
            });
        }
        if (role === 'audience') {
            if (this.localVideoPublished)
                await this.unpublishVideo();
            if (this.localAudioPublished)
                await this.unpublishAudio();
            if (this.localScreenShared)
                await this.stopScreenShare();
        }
        this.role = role;
        if (this.transport?.setRole)
            await this.transport.setRole(role);
        if (this.joinRequest)
            this.joinRequest = { ...this.joinRequest, role };
    }
    /** Snapshot of remote participants, used by the Agora-compatible facade. */
    getParticipantsSnapshot() {
        return [...this.participants.values()].map((participant) => ({
            uid: participant.uid,
            userId: participant.userId,
            role: participant.role,
            audio: participant.audio,
            video: participant.video,
        }));
    }
    /** Whether local audio/video is currently published. */
    getPublishState() {
        return {
            audio: this.localAudioPublished,
            video: this.localVideoPublished,
            screen: this.localScreenShared,
        };
    }
    getConnectionState() {
        return this.state;
    }
    getChannelName() {
        return this.channelName;
    }
    getLocalUid() {
        return this.localUid;
    }
    getUserId() {
        return this.userId;
    }
    getRole() {
        return this.role;
    }
    getLastError() {
        return this.lastError;
    }
    async getStats() {
        const transport = this.assertConnected('read stats');
        const stats = await transport.getStats();
        return publicStats(stats);
    }
    /**
     * Local capture stream for preview/rendering (camera by default, `screen` for the active share).
     * Returns null when nothing is published yet or the transport has no media pipeline (fake).
     */
    getLocalStream(source = 'camera') {
        return this.transport?.getLocalStream(source) ?? null;
    }
    /** Remote participant stream for rendering; null until published and subscribed. */
    getRemoteStream(uid, mediaType) {
        return this.transport?.getRemoteStream(uid, mediaType) ?? null;
    }
    /** Resolve numeric uids to IM user ids through the independently configured uid mapping chain. */
    async resolveUserIds(rtcUids) {
        return this.tokenProvider.getUserIdsWithRTCUids(rtcUids, this.channelName ?? undefined);
    }
    // ------------------------------------------------------------------ publishing
    async publishAudio() {
        const transport = this.assertConnected('publish audio');
        if (this.localAudioPublished)
            return;
        await transport.publish('audio');
        this.localAudioPublished = true;
        this.localAudioMuted = false;
    }
    async unpublishAudio() {
        const transport = this.assertConnected('unpublish audio');
        if (!this.localAudioPublished)
            return;
        await transport.unpublish('audio');
        this.localAudioPublished = false;
        this.localAudioMuted = false;
    }
    async muteAudio(muted) {
        const transport = this.assertConnected('mute audio');
        if (!this.localAudioPublished) {
            throw rtcError('invalid_parameter', 'audio is not published; call publishAudio() first');
        }
        await transport.setMuted('audio', muted);
        this.localAudioMuted = muted;
    }
    async publishVideo() {
        const transport = this.assertConnected('publish video');
        if (this.localVideoPublished)
            return;
        await transport.publish('video');
        this.localVideoPublished = true;
        this.localVideoMuted = false;
    }
    async unpublishVideo() {
        const transport = this.assertConnected('unpublish video');
        if (!this.localVideoPublished)
            return;
        await transport.unpublish('video');
        this.localVideoPublished = false;
        this.localVideoMuted = false;
    }
    async muteVideo(muted) {
        const transport = this.assertConnected('mute video');
        if (!this.localVideoPublished) {
            throw rtcError('invalid_parameter', 'video is not published; call publishVideo() first');
        }
        await transport.setMuted('video', muted);
        this.localVideoMuted = muted;
    }
    async startScreenShare() {
        const transport = this.assertConnected('share the screen');
        await transport.startScreenShare();
        this.localScreenShared = true;
    }
    async stopScreenShare() {
        if (!this.localScreenShared)
            return;
        if (this.transport)
            await this.transport.stopScreenShare();
        this.localScreenShared = false;
    }
    /**
     * Publish a track captured by the caller. Extension used by the Agora-shaped facade, whose
     * public flow is `AgoraRTC.createCameraVideoTrack()` followed by `client.publish(track)`.
     */
    async publishMediaTrack(track, source) {
        const transport = this.assertConnected('publish a local track');
        if (this.role === 'audience') {
            throw rtcError('RTC_PUBLISH_DENIED', 'audience participants cannot publish media');
        }
        if (transport.publishTrack) {
            await transport.publishTrack(track, source);
        }
        else {
            const mediaType = source === 'microphone' || source === 'screen-audio' ? 'audio' : 'video';
            const published = mediaType === 'audio' ? this.localAudioPublished : this.localVideoPublished;
            if (!published)
                await transport.publish(mediaType);
        }
        if (source === 'camera')
            this.localVideoPublished = true;
        else if (source === 'screen')
            this.localScreenShared = true;
        else
            this.localAudioPublished = true;
    }
    async unpublishMediaTrack(source) {
        if (this.state !== 'connected' || this.transport === null) {
            throw rtcError('channel_not_found', 'not in a channel', { sdkCode: -113 });
        }
        if (this.transport.unpublishTrack) {
            await this.transport.unpublishTrack(source);
        }
        else {
            const mediaType = source === 'microphone' || source === 'screen-audio' ? 'audio' : 'video';
            await this.transport.unpublish(mediaType);
        }
        if (source === 'camera')
            this.localVideoPublished = false;
        else if (source === 'screen')
            this.localScreenShared = false;
        else
            this.localAudioPublished = false;
    }
    async setVideoEncoderConfiguration(config) {
        validateEncoderConfiguration(config);
        this.encoderConfig = { ...config };
        if (this.transport)
            await this.transport.setVideoEncoderConfiguration(config);
    }
    async startPreview() {
        if (this.state !== 'disconnected' && this.state !== 'failed') {
            throw rtcError('invalid_parameter', 'preview is only available before joining a channel');
        }
        if (this.previewing)
            return;
        const transport = this.obtainTransport();
        this.wireTransport(transport);
        await transport.startPreview();
        this.previewing = true;
    }
    async stopPreview() {
        if (!this.previewing)
            return;
        if (this.transport)
            await this.transport.stopPreview();
        this.previewing = false;
    }
    async enableDualStreamMode(enabled) {
        this.dualStream = enabled;
        if (this.transport)
            await this.transport.enableDualStreamMode(enabled);
    }
    async setDynacast(enabled) {
        this.dynacast = enabled;
        if (this.transport)
            await this.transport.setDynacast(enabled);
    }
    async playAudioEffect(effect) {
        if (typeof effect !== 'string' || effect.length === 0) {
            throw rtcError('invalid_parameter', 'effect must be a non-empty string');
        }
        if (this.transport?.playAudioEffect) {
            await this.transport.playAudioEffect(effect);
            return;
        }
        this.emit('audio-effect-finished', { effect });
    }
    // ------------------------------------------------------------------ subscribing
    async subscribe(uid, mediaType) {
        validateUid(uid);
        validateSubscriptionMedia(mediaType);
        const transport = this.assertConnected('subscribe');
        await transport.subscribe(uid, mediaType);
    }
    async unsubscribe(uid, mediaType) {
        validateUid(uid);
        validateSubscriptionMedia(mediaType);
        const transport = this.assertConnected('unsubscribe');
        await transport.unsubscribe(uid, mediaType);
    }
    async setRemoteVideoStreamType(uid, streamType) {
        validateUid(uid);
        if (streamType !== 'high' && streamType !== 'low') {
            throw rtcError('invalid_parameter', "streamType must be 'high' or 'low'");
        }
        const transport = this.assertConnected('select a remote stream type');
        await transport.setRemoteVideoStreamType(uid, streamType);
    }
    async setSubscribeVisibleOnly(enabled) {
        this.visibleOnly = enabled;
        if (this.transport)
            await this.transport.setSubscribeVisibleOnly(enabled);
    }
    // ------------------------------------------------------------------ data
    async sendData(message) {
        if (!message || message.payload === undefined || message.payload === null) {
            throw rtcError('invalid_parameter', 'data payload is required');
        }
        if (typeof message.payload === 'string' && message.payload.length === 0) {
            throw rtcError('invalid_parameter', 'data payload must not be empty');
        }
        if (typeof message.payload !== 'string' && !(message.payload instanceof Uint8Array)) {
            throw rtcError('invalid_parameter', 'data payload must be a string or Uint8Array');
        }
        if (message.topic !== undefined) {
            if (typeof message.topic !== 'string' ||
                message.topic.length === 0 ||
                message.topic.length > 128) {
                throw rtcError('invalid_parameter', 'topic must be a string between 1 and 128 characters');
            }
        }
        if (message.to !== undefined) {
            const destinations = Array.isArray(message.to) ? message.to : [message.to];
            for (const uid of destinations)
                validateUid(uid);
        }
        const transport = this.assertConnected('send data');
        await transport.sendData({
            ...message,
            reliable: message.reliable ?? true,
        });
    }
    // ------------------------------------------------------------------ quality
    async startQualityMonitoring(options = {}) {
        if (this.channelName === null || this.localUid === null) {
            throw rtcError('channel_not_found', 'join a channel before starting quality monitoring', {
                sdkCode: -113,
            });
        }
        if (!this.qualityReporter) {
            const merged = { ...(this.config.quality ?? {}), ...options };
            const sink = merged.postSamples ?? merged.sink;
            this.qualityReporter = new QualityReporter({
                channelName: this.channelName,
                uid: this.localUid,
                product: merged.product ?? 'web',
                ...(this.config.dimensions || merged.dimensions
                    ? { dimensions: merged.dimensions ?? this.config.dimensions }
                    : {}),
                ...(sink ? { sink } : {}),
                ...(merged.endpoint ? { endpoint: merged.endpoint } : {}),
                clock: this.clock,
                logger: this.logger,
                ...(merged.maxBatchSize !== undefined ? { maxBatchSize: merged.maxBatchSize } : {}),
                ...(merged.maxRetries !== undefined ? { maxRetries: merged.maxRetries } : {}),
                ...(merged.retryDelayMs !== undefined ? { retryDelayMs: merged.retryDelayMs } : {}),
            });
            if (this.pendingJoinObservation) {
                this.qualityReporter.recordJoin(this.pendingJoinObservation);
                this.pendingJoinObservation = null;
            }
        }
        this.qualityReporter.start(options.intervalMs ?? 60_000);
    }
    async stopQualityMonitoring() {
        const reporter = this.qualityReporter;
        this.qualityReporter = null;
        if (reporter)
            await reporter.stop();
    }
    /** Release all timers and listeners. Does not disconnect silently: callers should leave first. */
    async dispose() {
        this.stopTimers();
        this.clearReconnectTimer();
        if (this.qualityReporter) {
            const reporter = this.qualityReporter;
            this.qualityReporter = null;
            await reporter.stop();
        }
        if (this.transport)
            await this.transport.dispose().catch(() => undefined);
        this.emitter.removeAll();
        this.participants.clear();
        this.state = 'disconnected';
    }
    // ------------------------------------------------------------------ internals
    applyToken(token, channelName) {
        if (token.appId !== this.config.appId) {
            throw rtcError('invalid_appid', `token was issued for appId ${token.appId}, expected ${this.config.appId}`);
        }
        if (token.channelName && token.channelName !== '*' && token.channelName !== channelName) {
            throw rtcError('token_channel_mismatch', 'token is scoped to a different channel');
        }
        this.token = token;
        this.localUid = token.rtcUid;
        const privilegeExpireAt = this.privilegeExpireAt(token);
        this.tokenTimingHost = { privilegeExpireAt };
        this.privilegeExpiredEmitted = false;
        this.privilegeWillExpireEmitted = false;
    }
    privilegeExpireAt(token) {
        const explicit = isoToEpochMs(token.privilegeExpireAt) ?? isoToEpochMs(token.expireAt);
        if (explicit !== null)
            return explicit;
        return parseTokenTiming(token.rtcToken).privilegeExpireAt;
    }
    resolveServerUrl(token) {
        return token.serverUrl ?? this.config.serverUrl ?? '';
    }
    buildJoinOptions(token, channelName, url, publishAudio, publishVideo) {
        return {
            url,
            token: token.rtcToken,
            appId: token.appId,
            channelName,
            uid: token.rtcUid,
            role: this.role,
            autoSubscribe: true,
            publishAudio,
            publishVideo,
            ...(this.config.videoDeviceId ? { videoDeviceId: this.config.videoDeviceId } : {}),
            ...(this.config.audioDeviceId ? { audioDeviceId: this.config.audioDeviceId } : {}),
            ...(this.encoderConfig ? { encoder: this.encoderConfig } : {}),
            dualStream: this.dualStream,
            dynacast: this.dynacast,
            subscribeVisibleOnly: this.visibleOnly,
            ...(this.config.iceServers?.length ? { iceServers: this.config.iceServers } : {}),
        };
    }
    obtainTransport() {
        if (this.transport)
            return this.transport;
        const mode = this.transportMode ?? 'auto';
        if (mode === 'fake') {
            this.transport = new FakeTransport({ clock: this.clock, logger: this.logger });
        }
        else {
            this.transport = new SfuTransport({ clock: this.clock, logger: this.logger });
        }
        return this.transport;
    }
    wireTransport(transport) {
        if (this.transportWired)
            return;
        this.transportWired = true;
        transport.on('connection-state', ({ state, reason }) => {
            this.handleTransportConnectionState(state, reason);
        });
        transport.on('participant-joined', (participant) => {
            void this.announceParticipant(participant);
        });
        transport.on('participant-left', ({ participant, reason }) => {
            this.participants.delete(participant.uid);
            this.freezeCursor.delete(participant.uid);
            this.emit('user-left', {
                uid: participant.uid,
                userId: participant.userId,
                reason,
            });
        });
        transport.on('track-published', ({ uid, mediaType, userId }) => {
            const participant = this.participants.get(uid);
            if (participant) {
                if (mediaType === 'audio')
                    participant.audio = true;
                else
                    participant.video = true;
            }
            this.emit('user-published', { uid, mediaType, userId });
        });
        transport.on('track-unpublished', ({ uid, mediaType, userId }) => {
            const participant = this.participants.get(uid);
            if (participant) {
                if (mediaType === 'audio')
                    participant.audio = false;
                else
                    participant.video = false;
            }
            this.emit('user-unpublished', { uid, mediaType, userId });
        });
        transport.on('track-muted', ({ uid, mediaType, muted }) => {
            this.emit('user-mute-changed', { uid, mediaType, muted });
        });
        transport.on('role-changed', ({ uid, role }) => {
            const participant = this.participants.get(uid);
            if (participant)
                participant.role = role;
            this.emit('user-role-changed', { uid, role });
        });
        transport.on('data-received', ({ from, payload, topic }) => {
            this.emit('data-received', { from, payload, topic });
        });
        transport.on('volume', ({ uid, level }) => {
            this.emit('volume-indicator', { uid, level });
        });
        transport.on('audio-effect-finished', ({ effect }) => {
            this.emit('audio-effect-finished', { effect });
        });
        transport.on('network-quality', ({ uid, uplink, downlink, delayMs }) => {
            this.emit('network-quality', {
                uid,
                uplink: clampQuality(uplink),
                downlink: clampQuality(downlink),
                delayMs,
            });
        });
    }
    async announceParticipant(incoming) {
        const existing = this.participants.get(incoming.uid);
        let userId = incoming.userId ?? existing?.userId ?? null;
        if (!userId && this.tokenProvider.hasUidMapping()) {
            try {
                const mapping = await this.tokenProvider.getUserIdsWithRTCUids([incoming.uid], this.channelName ?? undefined);
                userId = mapping[incoming.uid] ?? null;
            }
            catch (error) {
                this.logger.debug('rtc-web: uid resolution failed for participant', incoming.uid, error);
            }
        }
        const participant = {
            uid: incoming.uid,
            userId,
            role: incoming.role ?? existing?.role ?? 'host',
            audio: existing?.audio ?? false,
            video: existing?.video ?? false,
            screen: existing?.screen ?? false,
        };
        this.participants.set(incoming.uid, participant);
        if (!existing)
            this.emit('user-joined', { uid: participant.uid, userId });
    }
    handleTransportConnectionState(state, reason) {
        switch (state) {
            case 'connecting':
                if (this.state === 'disconnected' || this.state === 'failed') {
                    this.validatedTransition('connecting', reason ?? 'transport-connecting');
                }
                break;
            case 'connected':
                this.reconnectAttempt = 0;
                this.clearReconnectTimer();
                if (this.state !== 'connected')
                    this.validatedTransition('connected', reason ?? 'transport-connected');
                if (this.joinRequest)
                    this.startTimers();
                break;
            case 'reconnecting':
                this.validatedTransition('reconnecting', reason ?? 'transport-reconnecting');
                break;
            case 'failed':
                if (this.state === 'connecting' || this.leaving || !this.joinRequest) {
                    this.transition('failed', reason ?? 'transport-failed');
                }
                else {
                    this.scheduleReconnect(reason ?? 'transport-failed');
                }
                break;
            case 'disconnected':
                if (this.state === 'connecting' || this.leaving || !this.joinRequest) {
                    this.transition('disconnected', reason ?? 'transport-disconnected');
                }
                else {
                    this.scheduleReconnect(reason ?? 'transport-disconnected');
                }
                break;
            default:
                break;
        }
    }
    scheduleReconnect(reason) {
        if (this.leaving || !this.joinRequest)
            return;
        if (this.reconnectTimer !== null)
            return;
        if (this.reconnectAttempt >= this.reconnectMaxAttempts) {
            this.validatedTransition('failed', 'reconnect attempts exhausted');
            return;
        }
        this.reconnectAttempt += 1;
        this.validatedTransition('reconnecting', reason);
        const delay = Math.min(this.reconnectMaxDelayMs, this.reconnectBaseDelayMs * 2 ** (this.reconnectAttempt - 1));
        this.clearReconnectTimer();
        this.reconnectTimer = this.clock.setTimeout(() => {
            this.reconnectTimer = null;
            void this.attemptReconnect();
        }, delay);
        this.logger.info(`rtc-web: scheduling reconnect attempt ${this.reconnectAttempt}/${this.reconnectMaxAttempts} in ${delay}ms`);
    }
    async attemptReconnect() {
        if (this.leaving || !this.joinRequest)
            return;
        try {
            const token = await this.tokenProvider.getRTCTokenInfo(this.joinRequest);
            this.applyToken(token, this.joinRequest.channelName);
            const transport = this.obtainTransport();
            this.wireTransport(transport);
            const url = this.resolveServerUrl(token);
            if (!url && transport.kind !== 'fake') {
                throw rtcError('invalid_parameter', 'the media server URL is missing for reconnect');
            }
            const result = await transport.connect(this.buildJoinOptions(token, this.joinRequest.channelName, url, this.localAudioPublished, this.localVideoPublished));
            this.localUid = result.uid;
            for (const participant of result.participants)
                await this.announceParticipant(participant);
            this.lastStatsAt = this.clock.now();
            this.reconnectAttempt = 0;
            this.validatedTransition('connected', 'reconnected');
            this.startTimers();
        }
        catch (error) {
            this.lastError = normalizeError(error);
            this.logger.warn('rtc-web: reconnect attempt failed', this.lastError.message);
            this.scheduleReconnect('reconnect-attempt-failed');
        }
    }
    startTimers() {
        this.startStatsTimer();
        this.scheduleRenewal();
    }
    stopTimers() {
        this.clearStatsTimer();
        this.clearRenewTimer();
    }
    startStatsTimer() {
        this.clearStatsTimer();
        this.lastStatsAt = this.clock.now();
        this.statsTimer = this.clock.setInterval(() => {
            void this.refreshStats();
        }, this.statsIntervalMs);
    }
    clearStatsTimer() {
        if (this.statsTimer !== null) {
            this.clock.clearInterval(this.statsTimer);
            this.statsTimer = null;
        }
    }
    clearRenewTimer() {
        if (this.renewTimer !== null) {
            this.clock.clearTimeout(this.renewTimer);
            this.renewTimer = null;
        }
    }
    clearReconnectTimer() {
        if (this.reconnectTimer !== null) {
            this.clock.clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    scheduleRenewal() {
        this.clearRenewTimer();
        const privilegeExpireAt = this.tokenTimingHost.privilegeExpireAt;
        if (privilegeExpireAt === null)
            return;
        const leadMs = TOKEN_PRIVILEGE_WILL_EXPIRE_LEAD_SECONDS * 1000;
        const delay = Math.max(0, privilegeExpireAt - leadMs - this.clock.now());
        this.renewTimer = this.clock.setTimeout(() => {
            this.renewTimer = null;
            void this.handleRenewalDue(privilegeExpireAt);
        }, delay);
    }
    async handleRenewalDue(privilegeExpireAt) {
        if (!this.privilegeWillExpireEmitted) {
            this.privilegeWillExpireEmitted = true;
            this.emit('token-privilege-will-expire', { privilegeExpireAt });
        }
        if (this.config.autoRenew === false) {
            const remaining = privilegeExpireAt - this.clock.now();
            if (remaining > 0) {
                this.renewTimer = this.clock.setTimeout(() => {
                    this.renewTimer = null;
                    void this.handleRenewalDue(privilegeExpireAt);
                }, remaining);
                return;
            }
            if (!this.privilegeExpiredEmitted) {
                this.privilegeExpiredEmitted = true;
                this.emit('token-privilege-did-expire', {});
            }
            return;
        }
        const previousExpiry = privilegeExpireAt;
        if (!this.token || !this.joinRequest)
            return;
        try {
            const renewed = await this.tokenProvider.renewToken(this.token, this.joinRequest);
            this.applyToken(renewed, this.channelName ?? this.joinRequest?.channelName ?? '');
            if (this.transport)
                await this.transport.renewToken(renewed.rtcToken);
            const newExpiry = this.tokenTimingHost.privilegeExpireAt;
            if (newExpiry !== null && newExpiry > previousExpiry + 1000) {
                this.scheduleRenewal();
            }
            else {
                this.logger.warn('rtc-web: token renewal returned no later privilege expiry; auto-renewal is paused until the next join');
            }
        }
        catch (error) {
            this.lastError = normalizeError(error);
            this.logger.warn('rtc-web: token renewal failed', this.lastError.message);
            const remaining = privilegeExpireAt - this.clock.now();
            if (remaining > 0) {
                this.renewTimer = this.clock.setTimeout(() => {
                    this.renewTimer = null;
                    void this.handleRenewalDue(privilegeExpireAt);
                }, Math.min(5000, remaining));
            }
            else {
                if (!this.privilegeExpiredEmitted) {
                    this.privilegeExpiredEmitted = true;
                    this.emit('token-privilege-did-expire', {});
                }
                this.scheduleReconnect('token-privilege-expired');
            }
        }
    }
    async refreshStats() {
        if (this.state !== 'connected' || !this.transport)
            return;
        const transport = this.transport;
        const now = this.clock.now();
        const windowMs = Math.max(1, now - (this.lastStatsAt ?? now));
        this.lastStatsAt = now;
        const localUid = this.localUid;
        if (localUid !== null) {
            const stats = await transport.getStats().catch((error) => {
                this.logger.debug('rtc-web: local stats failed', error);
                return null;
            });
            if (stats) {
                this.emitTrackStats(localUid, stats);
                this.recordQualityFromStats(localUid, stats, windowMs, this.localAudioPublished, this.localVideoPublished);
                const quality = classifyStats(stats, true);
                this.emit('network-quality', {
                    uid: localUid,
                    uplink: quality,
                    downlink: quality,
                    delayMs: stats.rtt,
                });
            }
        }
        for (const participant of [...this.participants.values()]) {
            const stats = await transport.getStats(participant.uid).catch((error) => {
                this.logger.debug('rtc-web: remote stats failed', participant.uid, error);
                return null;
            });
            if (!stats)
                continue;
            this.emitTrackStats(participant.uid, stats);
            this.recordQualityFromStats(participant.uid, stats, windowMs, participant.audio, participant.video);
            const downlink = classifyStats(stats, true);
            this.emit('network-quality', {
                uid: participant.uid,
                uplink: NetworkQuality.Unknown,
                downlink,
                delayMs: stats.rtt,
            });
        }
    }
    emitTrackStats(uid, stats) {
        this.emit('track-stats', { uid, stats: publicStats(stats) });
    }
    recordQualityFromStats(uid, stats, windowMs, audioActive, videoActive) {
        const reporter = this.qualityReporter;
        if (!reporter)
            return;
        if (audioActive)
            reporter.recordActivity(uid, 'audio', windowMs);
        if (videoActive)
            reporter.recordActivity(uid, 'video', windowMs);
        const previous = this.freezeCursor.get(uid) ?? { video: 0, audio: 0 };
        const videoDelta = Math.max(0, stats.videoFreezeMs - previous.video);
        const audioDelta = Math.max(0, stats.audioFreezeMs - previous.audio);
        if (videoDelta > 0)
            reporter.recordFreeze(uid, 'video', videoDelta);
        if (audioDelta > 0)
            reporter.recordFreeze(uid, 'audio', audioDelta);
        this.freezeCursor.set(uid, { video: stats.videoFreezeMs, audio: stats.audioFreezeMs });
        reporter.recordNetworkDelay(uid, stats.rtt, windowMs);
    }
    recordJoinObservation(observation) {
        if (this.qualityReporter)
            this.qualityReporter.recordJoin(observation);
        else
            this.pendingJoinObservation = observation;
    }
    assertConnected(action) {
        if (this.state === 'reconnecting') {
            throw rtcError('RTC_RECONNECTING', `cannot ${action} while the connection is reconnecting`);
        }
        if (this.state !== 'connected' || !this.transport) {
            throw rtcError('channel_not_found', `cannot ${action} before joining a channel`, {
                sdkCode: -113,
            });
        }
        return this.transport;
    }
    transition(next, reason) {
        if (this.state === next)
            return;
        const previous = this.state;
        this.state = next;
        this.emitter.emit('connection-state-changed', { previous, current: next, reason });
    }
    /** Transition that also reports the same state with a new reason (reconnect attempts). */
    validatedTransition(next, reason) {
        if (this.state === next) {
            const previous = this.state;
            this.emitter.emit('connection-state-changed', { previous, current: next, reason });
            return;
        }
        this.transition(next, reason);
    }
    emit(event, payload) {
        this.emitter.emit(event, payload);
    }
}
function clampQuality(value) {
    const allowed = new Set(Object.values(NetworkQuality));
    return (allowed.has(value) ? value : NetworkQuality.Unknown);
}
function validateUid(uid) {
    if (!Number.isInteger(uid) || uid < 1 || uid > 2147483647) {
        throw rtcError('invalid_parameter', 'uid must be an integer in [1, 2147483647]');
    }
}
function validateSubscriptionMedia(mediaType) {
    if (mediaType !== 'audio' && mediaType !== 'video' && mediaType !== 'both') {
        throw rtcError('invalid_parameter', "mediaType must be 'audio', 'video' or 'both'");
    }
}
function validateEncoderConfiguration(config) {
    const positive = (value) => value === undefined || (Number.isFinite(value) && value > 0);
    if (!config ||
        !positive(config.width) ||
        !positive(config.height) ||
        !positive(config.frameRate) ||
        !Number.isFinite(config.width) ||
        !Number.isFinite(config.height) ||
        !Number.isFinite(config.frameRate)) {
        throw rtcError('invalid_parameter', 'width, height and frameRate must be positive numbers');
    }
    if (!positive(config.bitrateMin) || !positive(config.bitrateMax)) {
        throw rtcError('invalid_parameter', 'bitrateMin/bitrateMax must be positive when provided');
    }
    if (config.bitrateMin !== undefined &&
        config.bitrateMax !== undefined &&
        config.bitrateMin > config.bitrateMax) {
        throw rtcError('invalid_parameter', 'bitrateMin must be <= bitrateMax');
    }
    const preference = config.degradationPreference;
    if (preference !== undefined &&
        preference !== 'maintain-framerate' &&
        preference !== 'maintain-resolution' &&
        preference !== 'balanced') {
        throw rtcError('invalid_parameter', 'invalid degradationPreference');
    }
}
/** Create an SDK client. Throws synchronously only for invalid configuration. */
export function createClient(config) {
    return new RtcClient(config);
}
//# sourceMappingURL=client.js.map